/**
 * EditarMasivoModal - Modal para edición masiva de shipments tipo Excel
 *
 * Muestra una cuadrícula editable donde cada fila es un shipment
 * y cada columna es un campo editable (envío, preparación, COD, peso, nº líneas)
 *
 * Soporta copiar/pegar tipo Excel:
 * - Click para seleccionar una celda
 * - Ctrl/Cmd + Click para selección múltiple
 * - Ctrl/Cmd + C para copiar el valor de la celda activa
 * - Ctrl/Cmd + V para pegar en todas las celdas seleccionadas del mismo tipo
 */
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter } from '@shared/components/ui/ModalCenter';
import { faCopy, faPaste, faTableCells } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { ModalCenterRef } from '@shared/components/ui/ModalCenter/types';
import { useToast } from '@shared/hooks/use-toast';
import { cn } from '@shared/utils/utils';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface EditarMasivoModalRef {
  open: (data: EditarMasivoModalData) => void;
  close: () => void;
}

// Datos completos de cada shipment para la edición
export interface ShipmentForBulkEdit {
  id: number;
  reference: string;
  shop: string;
  client: string;
  lines: Array<{ id: number; concept: string; base_amount: number }>;
  weight_internal_kg: number;
  num_items: number;
}

export interface EditarMasivoModalData {
  shipments: ShipmentForBulkEdit[];
  onSuccess?: () => void;
}

// Estado editable de cada fila
interface RowValues {
  shippingPrice: string;
  preparationPrice: string;
  codPrice: string;
  weight: string;
  numItems: string;
}

// Identificador único de celda: "shipmentId-field"
type CellId = `${number}-${keyof RowValues}`;

// Sanitiza el input para precios: solo números, coma/punto y máximo 2 decimales
const sanitizePriceInput = (value: string): string => {
  let sanitized = value.replace('.', ',');
  sanitized = sanitized.replace(/[^0-9,]/g, '');
  const parts = sanitized.split(',');
  if (parts.length > 2) {
    sanitized = parts[0] + ',' + parts.slice(1).join('');
  }
  if (parts.length === 2 && parts[1].length > 2) {
    sanitized = parts[0] + ',' + parts[1].slice(0, 2);
  }
  return sanitized;
};

// Formatear número a string con coma
const formatNumber = (value: number | undefined): string => {
  if (value === undefined || value === null) return '';
  return value.toString().replace('.', ',');
};

// Celda editable de la cuadrícula con soporte de selección
const EditableCell = ({
  value,
  onChange,
  suffix,
  placeholder = '0,00',
  type = 'price',
  className,
  isSelected,
  onCellClick,
}: {
  value: string;
  onChange: (value: string) => void;
  suffix?: string;
  placeholder?: string;
  type?: 'price' | 'number';
  className?: string;
  isSelected?: boolean;
  onCellClick?: (e: React.MouseEvent) => void;
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (type === 'price') {
      onChange(sanitizePriceInput(e.target.value));
    } else {
      onChange(e.target.value.replace(/[^0-9]/g, ''));
    }
  };

  return (
    <div className={cn('relative', className)} onClick={onCellClick}>
      <Input
        type="text"
        value={value}
        onChange={handleChange}
        className={cn(
          'h-8 text-xs px-2 bg-background border-border/50 focus:border-primary transition-all',
          suffix && 'pr-7',
          isSelected && 'ring-2 ring-primary ring-offset-1 ring-offset-background border-primary bg-primary/5',
        )}
        placeholder={placeholder}
      />
      {suffix && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-complementary-text pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
  );
};

export const EditarMasivoModal = forwardRef<EditarMasivoModalRef>((_, ref) => {
  const { t } = useTranslation('invoicesOrdersModule');
  const { t: tCommon } = useTranslation('common');
  const modalRef = useRef<ModalCenterRef>(null);
  const currency = useCurrency();
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);

  const [modalData, setModalData] = useState<EditarMasivoModalData>({
    shipments: [],
    onSuccess: undefined,
  });

  // Estado de cada fila (Map: shipmentId -> valores)
  const [rowsData, setRowsData] = useState<Map<number, RowValues>>(new Map());

  // Estado para selección de celdas tipo Excel
  const [selectedCells, setSelectedCells] = useState<Set<CellId>>(new Set());
  const [lastSelectedCell, setLastSelectedCell] = useState<CellId | null>(null);
  const [clipboard, setClipboard] = useState<string>('');

  // Inicializar valores de las filas desde los shipments
  const initializeRows = useCallback((shipments: ShipmentForBulkEdit[]) => {
    const newRowsData = new Map<number, RowValues>();

    shipments.forEach((shipment) => {
      const shippingLine = shipment.lines.find((l) => l.concept === 'SHIPPING');
      const preparationLine = shipment.lines.find((l) => l.concept === 'PREPARATION');
      const codLine = shipment.lines.find((l) => l.concept === 'COD');

      newRowsData.set(shipment.id, {
        shippingPrice: formatNumber(shippingLine?.base_amount),
        preparationPrice: formatNumber(preparationLine?.base_amount),
        codPrice: formatNumber(codLine?.base_amount),
        weight: formatNumber(shipment.weight_internal_kg),
        numItems: shipment.num_items?.toString() || '',
      });
    });

    setRowsData(newRowsData);
    setSelectedCells(new Set());
    setLastSelectedCell(null);
    setClipboard('');
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      open: (data: EditarMasivoModalData) => {
        setModalData(data);
        initializeRows(data.shipments);
        modalRef.current?.openModal();
      },
      close: () => {
        modalRef.current?.closeModal();
      },
    }),
    [initializeRows],
  );

  // Campos editables en orden
  const editableFields: (keyof RowValues)[] = ['shippingPrice', 'preparationPrice', 'codPrice', 'weight', 'numItems'];

  // Manejar selección de celda
  const handleCellSelect = useCallback(
    (cellId: CellId, e: React.MouseEvent) => {
      e.stopPropagation();

      const [shipmentIdStr, field] = cellId.split('-') as [string, keyof RowValues];

      if (e.shiftKey && lastSelectedCell) {
        // Shift + Click: seleccionar rango desde última celda
        const [lastShipmentIdStr, lastField] = lastSelectedCell.split('-') as [string, keyof RowValues];

        const shipmentIds = modalData.shipments.map((s) => s.id);
        const startIdx = shipmentIds.indexOf(parseInt(lastShipmentIdStr, 10));
        const endIdx = shipmentIds.indexOf(parseInt(shipmentIdStr, 10));
        const startFieldIdx = editableFields.indexOf(lastField);
        const endFieldIdx = editableFields.indexOf(field);

        const rowStart = Math.min(startIdx, endIdx);
        const rowEnd = Math.max(startIdx, endIdx);
        const colStart = Math.min(startFieldIdx, endFieldIdx);
        const colEnd = Math.max(startFieldIdx, endFieldIdx);

        const newSet = new Set<CellId>();
        for (let r = rowStart; r <= rowEnd; r++) {
          for (let c = colStart; c <= colEnd; c++) {
            newSet.add(`${shipmentIds[r]}-${editableFields[c]}` as CellId);
          }
        }
        setSelectedCells(newSet);
      } else if (e.ctrlKey || e.metaKey) {
        // Ctrl/Cmd + Click: toggle selección múltiple
        setSelectedCells((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(cellId)) {
            newSet.delete(cellId);
          } else {
            newSet.add(cellId);
          }
          return newSet;
        });
        setLastSelectedCell(cellId);
      } else {
        // Click simple: seleccionar solo esta celda
        setSelectedCells(new Set([cellId]));
        setLastSelectedCell(cellId);
      }
    },
    [lastSelectedCell, modalData.shipments],
  );

  // Copiar valor de la primera celda seleccionada
  const handleCopy = useCallback(() => {
    if (selectedCells.size === 0) return;

    const firstCellId = Array.from(selectedCells)[0];
    const [shipmentIdStr, field] = firstCellId.split('-') as [string, keyof RowValues];
    const shipmentId = parseInt(shipmentIdStr, 10);
    const row = rowsData.get(shipmentId);

    if (row) {
      setClipboard(row[field]);
    }
  }, [selectedCells, rowsData]);

  // Pegar en todas las celdas seleccionadas
  const handlePaste = useCallback(() => {
    if (selectedCells.size === 0 || clipboard === undefined) return;

    setRowsData((prev) => {
      const newMap = new Map(prev);

      selectedCells.forEach((cellId) => {
        const [shipmentIdStr, field] = cellId.split('-') as [string, keyof RowValues];
        const shipmentId = parseInt(shipmentIdStr, 10);
        const currentRow = newMap.get(shipmentId);

        if (currentRow) {
          let sanitizedValue = clipboard;
          if (field === 'numItems') {
            sanitizedValue = clipboard.replace(/[^0-9]/g, '');
          } else {
            sanitizedValue = sanitizePriceInput(clipboard);
          }
          newMap.set(shipmentId, { ...currentRow, [field]: sanitizedValue });
        }
      });

      return newMap;
    });
  }, [selectedCells, clipboard]);

  // Escuchar atajos de teclado (Ctrl+C / Ctrl+V)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedCells.size === 0) return;

      const activeElement = document.activeElement;
      const isInputFocused = activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA';

      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        handleCopy();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'v' && !isInputFocused) {
        e.preventDefault();
        handlePaste();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedCells, handleCopy, handlePaste]);

  // Actualizar valor de una celda específica
  const updateCell = useCallback((shipmentId: number, field: keyof RowValues, value: string) => {
    setRowsData((prev) => {
      const newMap = new Map(prev);
      const currentRow = newMap.get(shipmentId);
      if (currentRow) {
        newMap.set(shipmentId, { ...currentRow, [field]: value });
      }
      return newMap;
    });
  }, []);

  // Verificar si hay cambios
  const hasChanges = useMemo(() => {
    for (const shipment of modalData.shipments) {
      const row = rowsData.get(shipment.id);
      if (!row) continue;

      const shippingLine = shipment.lines.find((l) => l.concept === 'SHIPPING');
      const preparationLine = shipment.lines.find((l) => l.concept === 'PREPARATION');
      const codLine = shipment.lines.find((l) => l.concept === 'COD');

      const original = {
        shippingPrice: formatNumber(shippingLine?.base_amount),
        preparationPrice: formatNumber(preparationLine?.base_amount),
        codPrice: formatNumber(codLine?.base_amount),
        weight: formatNumber(shipment.weight_internal_kg),
        numItems: shipment.num_items?.toString() || '',
      };

      if (
        row.shippingPrice !== original.shippingPrice ||
        row.preparationPrice !== original.preparationPrice ||
        row.codPrice !== original.codPrice ||
        row.weight !== original.weight ||
        row.numItems !== original.numItems
      ) {
        return true;
      }
    }
    return false;
  }, [modalData.shipments, rowsData]);

  const handleSave = useCallback(async () => {
    // Solo incluir shipments que tengan cambios reales
    const shipmentsPayload = modalData.shipments
      .map((shipment) => {
        const row = rowsData.get(shipment.id);
        if (!row) return null;

        // Obtener valores originales
        const shippingLine = shipment.lines.find((l) => l.concept === 'SHIPPING');
        const preparationLine = shipment.lines.find((l) => l.concept === 'PREPARATION');
        const codLine = shipment.lines.find((l) => l.concept === 'COD');

        const original = {
          shippingPrice: formatNumber(shippingLine?.base_amount),
          preparationPrice: formatNumber(preparationLine?.base_amount),
          codPrice: formatNumber(codLine?.base_amount),
          weight: formatNumber(shipment.weight_internal_kg),
          numItems: shipment.num_items?.toString() || '',
        };

        // Verificar si este shipment tiene cambios
        const hasShipmentChanges =
          row.shippingPrice !== original.shippingPrice ||
          row.preparationPrice !== original.preparationPrice ||
          row.codPrice !== original.codPrice ||
          row.weight !== original.weight ||
          row.numItems !== original.numItems;

        if (!hasShipmentChanges) return null;

        // Construir líneas actualizadas (solo las que cambiaron)
        const lines = shipment.lines
          .map((line) => {
            if (line.concept === 'SHIPPING' && row.shippingPrice !== original.shippingPrice) {
              return { id: line.id, base_amount: parseFloat(row.shippingPrice.replace(',', '.')) || 0 };
            }
            if (line.concept === 'PREPARATION' && row.preparationPrice !== original.preparationPrice) {
              return { id: line.id, base_amount: parseFloat(row.preparationPrice.replace(',', '.')) || 0 };
            }
            if (line.concept === 'COD' && row.codPrice !== original.codPrice) {
              return { id: line.id, base_amount: parseFloat(row.codPrice.replace(',', '.')) || 0 };
            }
            return null;
          })
          .filter((l): l is { id: number; base_amount: number } => l !== null);

        // Construir objeto del shipment para el bulk
        const shipmentData: {
          id: number;
          weight_internal_kg?: number;
          num_items?: number;
          lines?: Array<{ id: number; base_amount: number }>;
        } = { id: shipment.id };

        if (row.weight !== original.weight) {
          shipmentData.weight_internal_kg = parseFloat(row.weight.replace(',', '.')) || 0;
        }
        if (row.numItems !== original.numItems) {
          shipmentData.num_items = parseInt(row.numItems, 10) || 0;
        }
        if (lines.length > 0) {
          shipmentData.lines = lines;
        }

        return Object.keys(shipmentData).length > 1 ? shipmentData : null;
      })
      .filter((s): s is NonNullable<typeof s> => s !== null);

    if (shipmentsPayload.length === 0) {
      return;
    }

    setIsUpdating(true);

    try {
      const { ShipmentsApiAdapter } = await import('../../adapters/ShipmentsApiAdapter');
      const { createHttpClient } = await import('@adapters/HttpClient');
      const { apiClientWH } = await import('@shared/services/ApiClient');

      const adapter = new ShipmentsApiAdapter(createHttpClient(apiClientWH));
      const result = await adapter.bulkUpdateShipments(shipmentsPayload);

      if (result.failed === 0) {
        toast({
          title: t('toast.bulk_update_success.title', 'Actualización completada'),
          description: t('toast.bulk_update_success.description', {
            count: result.successful,
            defaultValue: `Se actualizaron ${result.successful} shipments correctamente`,
          }),
          variant: 'default',
        });
        modalRef.current?.closeModal();
        modalData.onSuccess?.();
      } else if (result.successful === 0) {
        // Obtener mensajes de error de los resultados
        const errorMessages = result.results
          .filter((r) => !r.success)
          .map((r) => r.message)
          .filter((v, i, a) => a.indexOf(v) === i) // Únicos
          .join(', ');

        toast({
          title: t('toast.bulk_update_error.title', 'Error al actualizar'),
          description:
            errorMessages ||
            t('toast.bulk_update_error.all_failed', {
              count: result.failed,
              defaultValue: `No se pudo actualizar ningún shipment`,
            }),
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('toast.bulk_update_partial.title', 'Actualización parcial'),
          description: t('toast.bulk_update_partial.description', {
            success: result.successful,
            failed: result.failed,
            defaultValue: `${result.successful} actualizados, ${result.failed} fallidos`,
          }),
          variant: 'default',
        });
        modalRef.current?.closeModal();
        modalData.onSuccess?.();
      }
    } catch {
      toast({
        title: t('toast.bulk_update_error.title', 'Error al actualizar'),
        description: t('toast.bulk_update_error.unexpected', 'Error inesperado al actualizar'),
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  }, [modalData, rowsData, toast, t]);

  return (
    <ModalCenter ref={modalRef} title={t('modals.editar_masivo.title', 'Edición masiva')} type="default" width={1400}>
      <div className="space-y-4">
        {/* Barra de herramientas */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-sm text-complementary-text">
            <FontAwesomeIcon icon={faTableCells} className="text-primary" />
            <span>
              {t('modals.editar_masivo.grid_title', 'Editando {{count}} shipments', {
                count: modalData.shipments.length,
              })}
            </span>
            {selectedCells.size > 0 && (
              <span className="text-primary font-medium">
                • {selectedCells.size} {selectedCells.size === 1 ? 'celda' : 'celdas'}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCopy}
              disabled={selectedCells.size === 0}
              className="h-8 px-3 text-xs"
            >
              <FontAwesomeIcon icon={faCopy} className="mr-1.5" />
              Copiar
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handlePaste}
              disabled={selectedCells.size === 0 || !clipboard}
              className="h-8 px-3 text-xs"
            >
              <FontAwesomeIcon icon={faPaste} className="mr-1.5" />
              Pegar
            </Button>
          </div>
        </div>

        {/* Cuadrícula tipo Excel */}
        <div className="border border-border rounded-lg overflow-hidden">
          {/* Header de la tabla */}
          <div className="grid grid-cols-[140px_160px_160px_1fr_1fr_1fr_90px_80px] bg-card-bg border-b border-border">
            <div className="px-3 py-2 text-xs font-semibold text-complementary-text border-r border-border">
              {t('modals.editar_masivo.col_reference', 'Referencia')}
            </div>
            <div className="px-3 py-2 text-xs font-semibold text-complementary-text border-r border-border">
              {t('table.shop', 'Tienda')}
            </div>
            <div className="px-3 py-2 text-xs font-semibold text-complementary-text border-r border-border">
              {t('table.client', 'Cliente')}
            </div>
            <div className="px-3 py-2 text-xs font-semibold text-complementary-text border-r border-border text-center">
              {t('modals.editar_prefactura.general.shipping_price', 'Envío')} ({currency})
            </div>
            <div className="px-3 py-2 text-xs font-semibold text-complementary-text border-r border-border text-center">
              {t('modals.editar_prefactura.general.preparation_price', 'Prep.')} ({currency})
            </div>
            <div className="px-3 py-2 text-xs font-semibold text-complementary-text border-r border-border text-center">
              {t('modals.editar_prefactura.general.cod_price', 'COD')} ({currency})
            </div>
            <div className="px-3 py-2 text-xs font-semibold text-complementary-text border-r border-border text-center">
              {t('modals.editar_prefactura.logistics.weight', 'Peso')}
            </div>
            <div className="px-3 py-2 text-xs font-semibold text-complementary-text text-center">
              {t('modals.editar_prefactura.logistics.num_items', 'Líneas')}
            </div>
          </div>

          {/* Filas de datos */}
          <div className="max-h-[60vh] overflow-y-auto">
            {modalData.shipments.map((shipment, index) => {
              const row = rowsData.get(shipment.id);
              if (!row) return null;

              return (
                <div
                  key={shipment.id}
                  className={cn(
                    'grid grid-cols-[140px_160px_160px_1fr_1fr_1fr_90px_80px] border-b border-border last:border-b-0',
                    index % 2 === 0 ? 'bg-background' : 'bg-card-bg/50',
                  )}
                >
                  {/* Referencia (solo lectura) */}
                  <div className="px-3 py-1.5 flex items-center border-r border-border">
                    <span className="text-xs font-medium text-text truncate" title={shipment.reference}>
                      {shipment.reference || `#${shipment.id}`}
                    </span>
                  </div>

                  {/* Tienda (solo lectura) */}
                  <div className="px-3 py-1.5 flex items-center border-r border-border">
                    <span className="text-xs text-complementary-text truncate" title={shipment.shop}>
                      {shipment.shop || '-'}
                    </span>
                  </div>

                  {/* Cliente (solo lectura) */}
                  <div className="px-3 py-1.5 flex items-center border-r border-border">
                    <span className="text-xs text-complementary-text truncate" title={shipment.client}>
                      {shipment.client || '-'}
                    </span>
                  </div>

                  {/* Envío */}
                  <div className="px-1 py-1 border-r border-border">
                    <EditableCell
                      value={row.shippingPrice}
                      onChange={(v) => updateCell(shipment.id, 'shippingPrice', v)}
                      isSelected={selectedCells.has(`${shipment.id}-shippingPrice`)}
                      onCellClick={(e) => handleCellSelect(`${shipment.id}-shippingPrice`, e)}
                    />
                  </div>

                  {/* Preparación */}
                  <div className="px-1 py-1 border-r border-border">
                    <EditableCell
                      value={row.preparationPrice}
                      onChange={(v) => updateCell(shipment.id, 'preparationPrice', v)}
                      isSelected={selectedCells.has(`${shipment.id}-preparationPrice`)}
                      onCellClick={(e) => handleCellSelect(`${shipment.id}-preparationPrice`, e)}
                    />
                  </div>

                  {/* COD */}
                  <div className="px-1 py-1 border-r border-border">
                    <EditableCell
                      value={row.codPrice}
                      onChange={(v) => updateCell(shipment.id, 'codPrice', v)}
                      isSelected={selectedCells.has(`${shipment.id}-codPrice`)}
                      onCellClick={(e) => handleCellSelect(`${shipment.id}-codPrice`, e)}
                    />
                  </div>

                  {/* Peso */}
                  <div className="px-1 py-1 border-r border-border">
                    <EditableCell
                      value={row.weight}
                      onChange={(v) => updateCell(shipment.id, 'weight', v)}
                      isSelected={selectedCells.has(`${shipment.id}-weight`)}
                      onCellClick={(e) => handleCellSelect(`${shipment.id}-weight`, e)}
                    />
                  </div>

                  {/* Nº líneas */}
                  <div className="px-1 py-1">
                    <EditableCell
                      value={row.numItems}
                      onChange={(v) => updateCell(shipment.id, 'numItems', v)}
                      type="number"
                      placeholder="0"
                      isSelected={selectedCells.has(`${shipment.id}-numItems`)}
                      onCellClick={(e) => handleCellSelect(`${shipment.id}-numItems`, e)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer con acciones */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="text-xs text-complementary-text">
            {hasChanges && (
              <span className="flex items-center gap-1.5 text-warning">
                <span className="w-1.5 h-1.5 bg-warning rounded-full animate-pulse" />
                {t('modals.editar_masivo.has_changes', 'Cambios sin guardar')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={() => modalRef.current?.closeModal()}
              disabled={isUpdating}
              data-testid="bulk-edit-cancel"
            >
              {tCommon('modal.cancel', 'Cancelar')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!hasChanges || isUpdating}
              data-testid="bulk-edit-save"
            >
              {isUpdating
                ? t('modals.editar_masivo.saving', 'Guardando...')
                : t('modals.editar_masivo.save', 'Guardar cambios')}
            </Button>
          </div>
        </div>
      </div>
    </ModalCenter>
  );
});

EditarMasivoModal.displayName = 'EditarMasivoModal';
